import type { AxiosInstance, AxiosRequestConfig } from "axios";

/**
 * Make identical concurrent GETs on `client` share one request.
 *
 * Several providers and views independently GET the same URL on the same render
 * pass — /campaign/:id (CampaignProvider + Sidenav), /reviews/config
 * (ToDoProvider + useDashboardData), keyword-setup and
 * keyword-insights/history (ToDoProvider + GoogleInsightsLayout).
 *
 * ponytail: in-flight only, nothing is kept once the request settles, so a
 * refetch that follows a mutation still goes to the network. GET-only on
 * purpose: POST bodies carry both the deliberate twin-period stats calls
 * (place-performance, chartData/google, users-stats) and every mutation.
 */
export function shareInFlightGets(client: AxiosInstance): void {
  const inFlight = new Map<string, Promise<unknown>>();
  const get = client.get.bind(client);

  client.get = ((url: string, config?: AxiosRequestConfig) => {
    const key = `${url}|${config ? JSON.stringify(config) : ""}`;
    const running = inFlight.get(key);
    if (running) return running;
    const request = get(url, config).finally(() => inFlight.delete(key));
    inFlight.set(key, request);
    return request;
  }) as typeof client.get;
}
